import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { labelsForKeys } from './version-labels';

/**
 * Which sections a version (or a session) changed. The detail (what was added and removed) is
 * marked in the rendered report itself.
 *
 * One line of tags. When they don't fit, the row scrolls sideways (mouse wheel, trackpad or the
 * arrow buttons) with no visible scrollbar, which would otherwise sit on top of the tags.
 */
const VersionChanges: React.FC<{ changedKeys: string[] }> = ({ changedKeys }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScroll({ left: el.scrollLeft > 0, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1 });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateArrows();
    const observer = new ResizeObserver(updateArrows);
    observer.observe(el);
    // A plain mouse wheel scrolls vertically; turn it into sideways scrolling for this row.
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX) || el.scrollWidth <= el.clientWidth) return;
      event.preventDefault();
      el.scrollLeft += event.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      observer.disconnect();
      el.removeEventListener('wheel', onWheel);
    };
  }, [changedKeys, updateArrows]);

  const scrollBy = (direction: 1 | -1) => {
    const el = scrollRef.current;
    el?.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  if (changedKeys.length === 0)
    return (
      <Typography variant='caption' color='text.secondary' noWrap>
        No changes compared with the version before it.
      </Typography>
    );

  return (
    <Stack direction='row' alignItems='center' sx={{ flex: 1, minWidth: 0 }}>
      {canScroll.left && (
        <IconButton size='small' aria-label='Scroll changes left' onClick={() => scrollBy(-1)} sx={{ flexShrink: 0 }}>
          <ChevronLeftIcon fontSize='small' />
        </IconButton>
      )}
      <Box
        ref={scrollRef}
        onScroll={updateArrows}
        sx={{
          display: 'flex',
          gap: 1,
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
          py: 0.5,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {labelsForKeys(changedKeys).map(label => (
          <Chip key={label} label={label} size='small' sx={{ flexShrink: 0 }} />
        ))}
      </Box>
      {canScroll.right && (
        <IconButton size='small' aria-label='Scroll changes right' onClick={() => scrollBy(1)} sx={{ flexShrink: 0 }}>
          <ChevronRightIcon fontSize='small' />
        </IconButton>
      )}
    </Stack>
  );
};

export default VersionChanges;
